import {useCallback, useEffect, useRef, useState} from 'react';

export interface FloatingWindowState {
  popped: boolean;
  floatPos: {x: number; y: number};
  floatSize: {w: number; h: number};
  handleDragStart: (e: React.MouseEvent) => void;
  handleResizeStart: (e: React.MouseEvent) => void;
  handlePopOut: () => void;
  handleDockBack: () => void;
}

export function useFloatingWindow(): FloatingWindowState {
  const [popped, setPopped] = useState(false);
  const [floatPos, setFloatPos] = useState({x: 100, y: 80});
  const [floatSize, setFloatSize] = useState({w: 420, h: 560});

  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const resizeRef = useRef<{
    startX: number;
    startY: number;
    origW: number;
    origH: number;
  } | null>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (dragRef.current) {
        setFloatPos({
          x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
          y: dragRef.current.origY + (e.clientY - dragRef.current.startY),
        });
      }
      if (resizeRef.current) {
        setFloatSize({
          w: Math.max(
            320,
            resizeRef.current.origW + (e.clientX - resizeRef.current.startX)
          ),
          h: Math.max(
            300,
            resizeRef.current.origH + (e.clientY - resizeRef.current.startY)
          ),
        });
      }
    };
    const onMouseUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: floatPos.x,
        origY: floatPos.y,
      };
    },
    [floatPos]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origW: floatSize.w,
        origH: floatSize.h,
      };
    },
    [floatSize]
  );

  const handlePopOut = useCallback(() => {
    setFloatPos({
      x: Math.max(40, (window.innerWidth - 420) / 2),
      y: Math.max(40, (window.innerHeight - 560) / 2 - 40),
    });
    setFloatSize({w: 420, h: 560});
    setPopped(true);
  }, []);

  const handleDockBack = useCallback(() => {
    setPopped(false);
  }, []);

  return {
    popped,
    floatPos,
    floatSize,
    handleDragStart,
    handleResizeStart,
    handlePopOut,
    handleDockBack,
  };
}
